import api from './api';
import { isMissingArtwork } from '../utils/formatDetection';
import { isBadCoverUrl } from '../utils/coverArt';
import {
  patchLocalAlbumMetadata,
  patchLocalArtistMetadata,
} from './localLibraryMetadata';

/** Skip re-enrichment for 7 days after ArtGate no-image or all providers failed. */
export const ENRICHMENT_RETRY_MS = 7 * 24 * 60 * 60 * 1000;

export function shouldRetryEnrichment(enrichmentAttemptedAt?: string | null): boolean {
  if (!enrichmentAttemptedAt) return true;
  const t = Date.parse(enrichmentAttemptedAt);
  if (!Number.isFinite(t)) return true;
  return Date.now() - t >= ENRICHMENT_RETRY_MS;
}

export function needsArtistPortrait(artist: {
  image_url?: string | null;
  enrichment_attempted_at?: string | null;
} | null | undefined): boolean {
  if (!artist?.name) return false;
  const url = String(artist.image_url || '').trim();
  if (url && !isBadCoverUrl(url)) return false;
  return shouldRetryEnrichment(artist.enrichment_attempted_at);
}

export function needsAlbumCover(album: {
  enrichment_attempted_at?: string | null;
} | null | undefined): boolean {
  if (!album) return false;
  if (!isMissingArtwork(album)) return false;
  return shouldRetryEnrichment(album.enrichment_attempted_at);
}

function enrichmentTimestamp(): string {
  return new Date().toISOString();
}

export type EnrichmentResult = {
  image_url?: string;
  cover_art_url?: string;
  source: string;
  /** ArtGate stable no-image — negative-cache via enrichment_attempted_at. */
  result?: 'image' | 'no-image';
  gate_reason?: string | null;
};

/** Qobuz → MusicBrainz mirror → Wikipedia. First hit wins. */
export async function resolveArtistPortrait(name: string): Promise<EnrichmentResult | null> {
  const q = encodeURIComponent(name.trim());
  if (!q) return null;

  const qobuz = await api.get(`/api/library/enrich/qobuz/artist?name=${q}`).catch(() => null);
  if (qobuz?.image_url) {
    return { image_url: qobuz.image_url, source: qobuz.source || 'qobuz' };
  }

  const mirror = await api.get(`/api/mirror/artist?name=${q}`).catch(() => null);
  if (mirror?.image_url) {
    return { image_url: mirror.image_url, source: mirror.source || 'mirror' };
  }

  const wiki = await api.get(`/api/library/enrich/wikipedia/artist?name=${q}`).catch(() => null);
  if (wiki?.image_url) {
    return { image_url: wiki.image_url, source: wiki.source || 'wikipedia' };
  }

  return null;
}

/** Identity-first ArtGate — single gated endpoint (MusicBrainz → Qobuz → MB-validated Wikipedia). */
export async function resolveAlbumCover(
  title: string,
  artist?: string | null,
  options?: { synthetic?: boolean; compilation?: boolean },
): Promise<EnrichmentResult | null> {
  if (!String(title || '').trim()) return null;

  const qs = new URLSearchParams();
  qs.set('title', title.trim());
  if (String(artist || '').trim()) qs.set('artist', String(artist).trim());
  if (options?.synthetic) qs.set('synthetic', '1');
  if (options?.compilation) qs.set('compilation', '1');

  const resp = await api.get(`/api/library/enrich/album?${qs.toString()}`).catch(() => null);
  if (!resp) return null;

  if (resp.result === 'no-image' || !resp.cover_art_url) {
    return {
      result: 'no-image',
      source: resp.source || 'none',
      gate_reason: resp.gate_reason || null,
    };
  }

  return {
    result: 'image',
    cover_art_url: resp.cover_art_url,
    source: resp.source || 'musicbrainz',
    gate_reason: null,
  };
}

export async function persistArtistPortrait(
  artistId: string,
  result: EnrichmentResult,
): Promise<void> {
  await patchLocalArtistMetadata(artistId, {
    image_url: result.image_url,
    image_source: result.source,
  });
}

export async function markArtistEnrichmentFailed(artistId: string): Promise<void> {
  await patchLocalArtistMetadata(artistId, {
    enrichment_attempted_at: enrichmentTimestamp(),
  });
}

export async function persistAlbumCover(
  albumId: string,
  result: EnrichmentResult,
): Promise<{ cover_hash?: string | null }> {
  return patchLocalAlbumMetadata(albumId, {
    cover_art_url: result.cover_art_url,
  });
}

export async function markAlbumEnrichmentFailed(albumId: string): Promise<void> {
  await patchLocalAlbumMetadata(albumId, {
    enrichment_attempted_at: enrichmentTimestamp(),
  });
}

/** Run tasks with a fixed concurrency pool. */
export async function runEnrichmentPool<T>(
  items: T[],
  worker: (item: T) => Promise<void>,
  concurrency = 4,
  isCancelled?: () => boolean,
): Promise<void> {
  if (!items.length) return;
  let idx = 0;
  const workers = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (!isCancelled?.() && idx < items.length) {
      const i = idx++;
      try {
        await worker(items[i]);
      } catch {
        /* single-item failure */
      }
    }
  });
  await Promise.all(workers);
}
